import alloy from '@alloyidentity/web-sdk';
import { isLeft } from 'fp-ts/lib/Either';
import {
  IDUploadJourneyResponse,
  JourneyApplicationStatus,
} from '@ads-bread/shared/bread/codecs';
import { shouldUseMockProvider } from '../../../../lib/mirage/utils';
import { StartIDUploadJourneyResult } from './types/actors';
interface AlloySDKData {
  journey_application_status: JourneyApplicationStatus;
  status: 'completed';
}

type MockAlloy = typeof alloy;

export const NEURO_SITE_ID_MOCK = 'foobarbaz';
export const NUERO_USER_ID_MOCK = 'gordon.freeman@blackmesa.gov';
export const IOVATION_BLACKBOX_ID_MOCK = 'blackbox-id-mock';

export const mockAlloy: MockAlloy = {
  init: (_: unknown) => {
    return Promise.resolve({
      neuroIdSiteId: NEURO_SITE_ID_MOCK,
      neuroUserId: NUERO_USER_ID_MOCK,
      iovationBlackboxId: IOVATION_BLACKBOX_ID_MOCK,
    });
  },
  preload: (_?: string) => {
    // noOp
  },
  open: (cb: (data: unknown) => void, anchorElement?: string) => {
    const container = document.getElementById(anchorElement || '');
    if (!container) {
      throw new Error('Could not find container for mock alloy!');
    }

    const buttonClasses = [
      'button-text-theme-primary',
      'bg-theme-primary',
      'border',
      'button-border-theme-primary',
      'm-2',
      'font-bold',
      'p-2',
      'drop-shadow-[0_5px_0px_rgba(78,92,111,0.25)]',
    ];
    const approvedButtonElement = document.createElement('button');
    approvedButtonElement.classList.add(...buttonClasses);
    approvedButtonElement.setAttribute('data-cy', 'alloy-simulate-approved');
    approvedButtonElement.textContent = 'Simulate Alloy Upload Complete';
    approvedButtonElement.addEventListener('click', () => {
      const d: AlloySDKData = {
        journey_application_status: JourneyApplicationStatus.Pending,
        status: 'completed',
      };
      cb(d);
    });

    const unknownButtonElement = document.createElement('button');
    unknownButtonElement.classList.add(...buttonClasses);
    unknownButtonElement.setAttribute('data-cy', 'alloy-simulate-unknown');
    unknownButtonElement.textContent = 'Simulate Unknown Response';
    unknownButtonElement.addEventListener('click', () => {
      const d = {
        foo: 'bar',
        baz: true,
        num: 8675309,
      };
      cb(d);
    });

    container.append(approvedButtonElement);
    container.append(unknownButtonElement);
  },
  close: () => {
    // noOp
  },
  createJourneyApplication: (_: unknown) => {
    return Promise.resolve();
  },
  createEvaluation: (_: unknown) => {
    return Promise.resolve();
  },
  getPublicUrl: () => '',
};

interface InitAlloyLibParams {
  journeyToken: string;
  journeyApplicationToken: string;
  journeySdkKey: string;
  isProduction: boolean;
  primaryColor: string;
  secondaryColor: string;
}

interface WaitForIDUploadJourneyResultParams {
  alloyLib: typeof alloy;
  applicationId: string;
  applicationStatusCodes: StartIDUploadJourneyResult['applicationStatusCodes'];
  domID: string;
  onJourneyData: (data: unknown) => void;
}

/**
 * Initializes the Alloy library with the provided parameters.
 * If the mock provider is enabled, it uses the mock implementation;
 * otherwise, it uses the real Alloy library.
 * @param param0 InitAlloyLibParams
 * @returns A promise that resolves to the initialized Alloy library instance.
 */
export const initAlloyLib = async ({
  journeyToken,
  journeyApplicationToken,
  journeySdkKey: sdkKey,
  isProduction,
  primaryColor: checkoutPrimaryColor,
  secondaryColor: checkoutSecondaryColor,
}: InitAlloyLibParams): Promise<typeof alloy> => {
  const alloyLib = shouldUseMockProvider() ? mockAlloy : alloy;

  await alloyLib.init({
    key: sdkKey,
    production: isProduction,
    journeyApplicationToken: journeyApplicationToken,
    journeyToken: journeyToken,
    /**
     * This flag is for multi applicant flows. If set to true we would need to
     * init alloy with the Entity ID of the applicant so that only their portion
     * would be displayed. As of 07/2024 we do not support multi applicant flows.
     */
    isSingleEntity: false,
    showHeader: true,
    apiUrl: 'https://docv.alloy.co/',
    appUrl: 'https://alloysdk.alloy.co',
    color: {
      primary: checkoutPrimaryColor || '',
      secondary: checkoutSecondaryColor || '',
    },
  });

  return alloyLib;
};

export const waitForIDUploadJourneyResult = async ({
  alloyLib,
  applicationId,
  applicationStatusCodes,
  domID,
  onJourneyData,
}: WaitForIDUploadJourneyResultParams): Promise<StartIDUploadJourneyResult> => {
  return await new Promise<StartIDUploadJourneyResult>((resolve, reject) => {
    const callback = (data: unknown) => {
      onJourneyData(data);

      alloyLib.close();

      const decoded = IDUploadJourneyResponse.decode(data);

      if (isLeft(decoded)) {
        reject(
          new Error('Unable to decode fraud alloy ID Upload data!', {
            cause: data,
          })
        );
      } else {
        resolve({
          applicationId,
          applicationStatusCodes,
          alloyJourneyApplicationStatus:
            decoded.right.journey_application_status,
        });
      }
    };

    alloyLib.open(callback, domID);
  });
};
